import { Body2, Button, ButtonType, logger, Subtitle1, useAlert, useTheme, useUser } from "capsule"
import React, { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { ActivityIndicator, Platform, StyleSheet, View } from "react-native"
import RNFS from "react-native-fs"
import { SafeAreaView } from "react-native-safe-area-context"
import Share from "react-native-share"

import { FhirExportResult } from "../../../functions/src/models/ExportTypes"
import { functions } from "../../capsule/features/Firebase/firebase"
import { ANDROID_EMULATOR_HOST_ALIAS } from "../../features/config/Constants"
import { SettingsNS } from "../../features/i18n/constants"
import { maxWidth } from "../../ThemeApp"

const ExportDataScreen = () => {
  const { user } = useUser()
  const { t } = useTranslation()
  const { showDialog, showSnack } = useAlert()
  const [isLoading, setIsLoading] = useState(false)

  const {
    colors: {
      black,
      primary,
      surface: { background },
    },
    dimensions: { spacing },
    fontMaker,
  } = useTheme()

  const s = useMemo(
    () =>
      StyleSheet.create({
        safe: {
          paddingLeft: spacing * 2,
          paddingRight: spacing * 2,
          backgroundColor: background,
        },
        paragraph: {
          paddingBottom: spacing,
          color: black.mediumEmphasis,
        },
        title: {
          paddingBottom: spacing / 2,
          color: black.highEmphasis,
        },
        bold: fontMaker({ weight: "Bold" }),
        dialogTextColor: {
          color: black.mediumEmphasis,
        },
        screenStyle: {
          paddingTop: 0,
          backgroundColor: background,
          ...(Platform.OS === "web" && { width: maxWidth }),
        },
        loader: {
          marginTop: spacing,
        },
        container: {
          width: "100%",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "space-evenly",
          paddingTop: spacing * 2.5,
        },
        loadingText: {
          marginTop: spacing * 2,
          textAlign: "center",
        },
      }),
    [spacing, fontMaker, black, background],
  )

  const handleExportData = useCallback(async () => {
    try {
      setIsLoading(true)

      logger("Demande d'extraction des données patient effectuée", {
        userId: user?.uid,
        timestamp: new Date().toISOString(),
      })

      // Appel de la Cloud Function — retourne une URL signée GCS
      const exportPatientDataFunction = functions().httpsCallable<unknown, FhirExportResult>(
        "exportPatientData",
      )
      const result = await exportPatientDataFunction()
      const { downloadUrl: rawDownloadUrl, filename, exportedAt, resourceCount } = result.data

      logger("Bundle FHIR généré avec succès", {
        exportedAt,
        resourceCount,
        filename,
      })

      const downloadUrl =
        Platform.OS === "android"
          ? rawDownloadUrl.replace("localhost", ANDROID_EMULATOR_HOST_ALIAS)
          : rawDownloadUrl

      if (Platform.OS === "web") {
        // Web : ouvrir l'URL signée pour déclencher le téléchargement
        const a = document.createElement("a")
        a.href = downloadUrl
        a.download = filename
        a.click()

        setIsLoading(false)
        showSnack({ message: t(`${SettingsNS}:exportDataSuccess`) })
      } else {
        // Mobile : télécharger depuis l'URL signée GCS vers le cache local
        // CachesDirectoryPath ne nécessite pas de permission (compatible Android 13+)
        const savePath =
          Platform.OS === "ios"
            ? `${RNFS.DocumentDirectoryPath}/${filename}`
            : `${RNFS.CachesDirectoryPath}/${filename}`

        await RNFS.downloadFile({
          fromUrl: downloadUrl,
          toFile: savePath,
        }).promise

        logger("Fichier FHIR sauvegardé en cache", { path: savePath, platform: Platform.OS })

        setIsLoading(false)

        // Ouvrir le menu de partage — l'utilisateur choisit où enregistrer
        // (Drive, Fichiers, email, etc.)
        Share.open({
          url: Platform.OS === "ios" ? savePath : `file://${savePath}`,
          type: "application/json",
          filename,
          saveToFiles: true,
        })
          .then(() => {
            showSnack({ message: t(`${SettingsNS}:exportDataSuccess`) })
          })
          .catch(err => {
            // L'utilisateur a fermé le menu sans partager — fichier déjà en cache
            logger("Menu de partage fermé par l'utilisateur", err)
            showSnack({ message: t(`${SettingsNS}:exportDataSuccess`) })
          })
      }
    } catch (error) {
      setIsLoading(false)
      logger("Erreur lors de l'export des données:", error)

      showDialog({
        type: "button",
        title: t(`${SettingsNS}:exportData`),
        message: t(`${SettingsNS}:exportDataError`),
        messageStyle: s.dialogTextColor,
        positive: {
          type: ButtonType.POSITIVE,
          label: "OK",
        },
      })
    }
  }, [user, showDialog, showSnack, t, s.dialogTextColor])

  return (
    <SafeAreaView style={[styles.safe, s.safe]}>
      <View style={s.screenStyle}>
        <View>
          <Subtitle1 style={s.title}>{t(`${SettingsNS}:exportDataDescription`)}</Subtitle1>
        </View>

        <View style={s.container}>
          {isLoading ? (
            <View style={s.loader}>
              <ActivityIndicator size="large" color={primary} />
              <Body2 style={[s.paragraph, s.loadingText]}>
                {t(`${SettingsNS}:exportDataLoading`)}
              </Body2>
            </View>
          ) : (
            <Button testID="export-data-btn" onPress={handleExportData} disabled={isLoading}>
              {t(`${SettingsNS}:exportData`)}
            </Button>
          )}
        </View>
      </View>
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  safe: {
    alignItems: "center",
    flex: 1,
  },
})

export default ExportDataScreen
